import { createAsyncThunk, createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import axiosInstance from '../../../../../app/config/axiosInstance';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import URLs from '../../../../../utils/appURLs';

const serviceContext = process.env.REACT_APP_SERVICE_CONTEXT;

interface VehicleList {
  pageno: number;
  search?: string;
  signal?: any;
  pagesize?: number;
}

const initialState = {
  isLoading: false,
  data: [],
  error: null,
  count: 0
};

// Action function to get vehicle details:
export const getVehicleListDetailsAction = createAsyncThunk<
  AxiosResponse<any>,
  VehicleList
>('vehicle list /get', async (data, thunkApi) => {
  const { pageno, search, signal, pagesize } = data;
  const queryParams: Record<string, string | number | boolean | undefined> = {
    pageno,
    search,
    pagesize
  };

  const filteredParams = Object.entries(queryParams)
    .filter(([key, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
    .join('&');
  try {
    const response = await axiosInstance({
      method: 'GET',
      url: `${serviceContext}${URLs.GET_VEHICLE_LIST_DETAILS}${filteredParams}`,
      signal: signal
    });
    return response.data;
  } catch (error: any) {
    if (error?.message !== 'canceled')
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
    return thunkApi.rejectWithValue(error);
  }
});

// Action function to get single vehicle details:
export const getVehicleSingleDetailsAction = createAsyncThunk<AxiosResponse<any>, string>(
  'vehicle single /get',
  async (vehicleNo, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${serviceContext}${URLs.GET_SINGLE_VEHICLE_DETAILS}vehicleNumber=${vehicleNo}`
      });
      return response.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// vehicle details slice
const vehicleListDetailsSlice = createSlice({
  name: 'vehicle-list',
  initialState,
  reducers: {
    cleargetVehicleListDetailsAction: state => {
      state.data = [];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getVehicleListDetailsAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getVehicleListDetailsAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        const newData: any = current(state)?.data ? current(state as any)?.data : [];
        state.data = [...newData, ...action.payload?.data?.vehicleDetails] as any;
        state.count = action.payload?.data?.count;
        state.error = null;
      })
      .addCase(getVehicleListDetailsAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

// single vehicle details slice
const vehicleSingleDetailsSlice = createSlice({
  name: 'vehicle-single',
  initialState,
  reducers: {
    clearVehicleDetails: (state: any) => {
      state.data = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getVehicleSingleDetailsAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getVehicleSingleDetailsAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.error = null;
      })
      .addCase(getVehicleSingleDetailsAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

export const vehcileDetailsSlice = vehicleListDetailsSlice.reducer;
export const singleVehcileDetailsSlice = vehicleSingleDetailsSlice.reducer;
export const { clearVehicleDetails } = vehicleSingleDetailsSlice.actions;
export const { cleargetVehicleListDetailsAction } = vehicleListDetailsSlice.actions;
